import { CiSettings } from "react-icons/ci";
import { formatDisplayName } from "../utils/textUtils";

interface SidebarProps {
  sensors: any[];
  selectedSystemId?: string | number | null;
  handleSelectedSensor: (system: any) => void;
  isOpen?: boolean;
  setIsOpen?: (value: boolean) => void;
}

const Sidebar = ({
  sensors,
  selectedSystemId,
  handleSelectedSensor,
}: SidebarProps) => {
  return (
    <aside
      className="flex-shrink-0 rounded-2xl overflow-hidden flex flex-col w-full lg:w-72"
      style={{
        background: "rgba(13,21,38,0.75)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: "1px solid rgba(255,255,255,0.07)",
        boxShadow: "0 8px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 sm:px-5 py-3.5 flex-shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-violet-500" />
          </span>
          <h2 className="text-sm font-semibold text-white tracking-wide">Systems</h2>
        </div>
        <span
          className="text-[10px] font-semibold px-2.5 py-1 rounded-full"
          style={{
            background: "rgba(139,92,246,0.12)",
            border: "1px solid rgba(139,92,246,0.25)",
            color: "#a78bfa",
          }}
        >
          {sensors?.length || 0} found
        </span>
      </div>

      {/*
        On mobile/tablet: horizontal scrolling pill row
        On lg+: vertical scrollable list
      */}
      <div className="flex lg:flex-col overflow-x-auto lg:overflow-x-hidden overflow-y-hidden lg:overflow-y-auto p-2 gap-2 lg:gap-1 custom-scrollbar flex-1">
        {(!sensors || sensors.length === 0) && (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-slate-600 w-full">
            <CiSettings className="w-9 h-9 mb-2 opacity-20" />
            <p className="text-xs text-center">No systems found</p>
          </div>
        )}

        {sensors?.map((system: any) => {
          const active = system.id === selectedSystemId;
          return (
            <div
              key={system.id}
              onClick={() => handleSelectedSensor(system)}
              className="flex-shrink-0 lg:flex-shrink cursor-pointer transition-all duration-200 rounded-xl group"
              style={{
                background: active
                  ? "linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%)"
                  : "rgba(255,255,255,0.02)",
                border: active
                  ? "1px solid rgba(139,92,246,0.35)"
                  : "1px solid rgba(255,255,255,0.05)",
                boxShadow: active ? "0 4px 16px rgba(99,102,241,0.15)" : "none",
                minWidth: "160px",
              }}
              title={system.description}
            >
              <div className="flex items-center gap-2.5 px-3 py-3 lg:px-4">
                <div className="relative flex-shrink-0">
                  <div
                    className={`w-2 h-2 rounded-full transition-colors ${
                      active ? "bg-violet-400" : "bg-slate-600"
                    }`}
                  />
                  {active && (
                    <div className="absolute inset-0 rounded-full bg-violet-400 animate-ping opacity-50" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-medium truncate transition-colors ${
                      active ? "text-white" : "text-slate-300 group-hover:text-white"
                    }`}
                  >
                    {formatDisplayName(system.name)}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5 truncate hidden lg:block">
                    {system.description || "No description"}
                  </p>
                </div>

                <span
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 transition-all"
                  style={{
                    background: active ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.06)",
                    border: active ? "1px solid rgba(139,92,246,0.3)" : "1px solid rgba(255,255,255,0.08)",
                    color: active ? "#c4b5fd" : "#64748b",
                  }}
                >
                  {system.sensors?.length || 0}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default Sidebar;
